import { CountryCode, Invoice, InvoiceMap } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useApiCampaignActivityInfoImmutable } from '@hooks-api/fortune/useApiCampaignActivityInfoImmutable'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { Button } from '@pets-ui/Button/Button'
import { Checkbox } from '@pets-ui/Checkbox/Checkbox'
import { ArrowRightSmall } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { ThemeProvider } from '@pets-ui/ThemeProvider/ThemeProvider'
import { colorTheme as fastpassColorTheme } from '@pets-ui/themes/fastpass'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { fieldRefHelper } from '@utils/fieldRefHelper'
import { useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CardSection } from '../../../../../components/CardSection/CardSection'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'
import { DialogInvoiceCategory } from './DialogInvoiceCategory'

const styles = stylex.create({
  invoice: {
    borderColor: colors.grey300,
    justifyContent: 'flex-start',
    paddingRight: 36,
    width: '100%',
  },
  endIcon: {
    position: 'absolute',
    right: 16,
  },
  checkbox: {
    color: colors.grey500,
  },
  buttonDisabled: {
    backgroundColor: colors.grey200,
  },
  invoiceError: {
    borderColor: colors.red600,
  },
})

export function InvoiceCategory() {
  const { control } = useFormContext<CheckoutOrder>()
  const [open, setOpen] = useState(false)
  const formValues = useWatch<CheckoutOrder>()

  const { isInRetryPaymentPage, campaignCode } = useCheckout()
  const { destructuredData: activityInfo } = useApiCampaignActivityInfoImmutable(campaignCode)
  const shouldDisplayInvoice = activityInfo?.region === CountryCode.TW
  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const validation = useCallbackEvent(() => {
    const isReceiptTypeFilled = !!formValues.receipt_type
    if (!isReceiptTypeFilled) return '此為必填欄位'

    const { receipt_type, business_ubn, business_ubn_title } = formValues
    const isBusinessEinvoice = receipt_type === Invoice.BUSINESS_EINVOICE
    if (!isBusinessEinvoice) return true

    const isBusinessUbnFilled = business_ubn && business_ubn_title
    return isBusinessUbnFilled ? true : '請完整填寫欄位'
  })

  if (!shouldDisplayInvoice) return null
  return (
    <>
      <ThemeProvider theme={fastpassColorTheme}>
        <CardSection title="發票種類">
          <VStack gap={2} alignItems="flex-start" width="100%">
            <Controller
              control={control}
              rules={{ validate: validation }}
              name="receiptTypeValidation"
              render={({ field: { ref }, fieldState: { error } }) => {
                return (
                  <VStack rootRef={fieldRefHelper(ref)} width="100%" gap={2}>
                    <Button
                      data-testid="button-checkout-open-invoice-dialog"
                      fullWidth
                      variant="outlined"
                      color="grey"
                      endIcon={<ArrowRightSmall />}
                      rootStyle={[styles.invoice, isInRetryPaymentPage && styles.buttonDisabled, error && styles.invoiceError]}
                      xStyles={{
                        endIcon: styles.endIcon,
                      }}
                      onClick={() => {
                        handleOpen()
                      }}
                      disabled={isInRetryPaymentPage}
                    >
                      <Text
                        variant="body"
                        color={isInRetryPaymentPage ? colors.grey500 : colors.black}
                      >
                        {(formValues.receipt_type && InvoiceMap[formValues.receipt_type]) || '請選擇'}
                      </Text>
                    </Button>
                    {error && (<Text color={colors.red600}>{error.message}</Text>)}
                  </VStack>
                )
              }}
            />
            <Controller
              control={control}
              name="refund_statement"
              render={({ field }) => (
                <Checkbox
                  onChange={field.onChange}
                  label="本人同意退換貨、訂單金額變動時，發票將由自力耕生股份有限公司全權處理作廢、折讓。"
                  checked={!!formValues.refund_statement}
                  rootStyle={styles.checkbox}
                  disabled={isInRetryPaymentPage}
                  xStyles={{
                    base: {
                      label: styles.checkbox,
                    },
                  }}
                />
              )}
            />
          </VStack>
        </CardSection>
        <DialogInvoiceCategory open={open} onClose={handleClose} />
      </ThemeProvider>
    </>

  )
}
